import { useEffect, useRef } from "react";

/**
 * Company-scoped window-focus scheduler.
 *
 * Owns subscription, single-flight, repeated-focus dedupe, busy coalescing,
 * one post-settle refresh, and unmount cancellation.
 *
 * Does not decide permissions, actor/company/resource match, membership
 * generation, protected-data clearing, or denied/not-found transitions.
 * The surface callback owns that policy and must use scopeSession.
 */

export function createCompanyFocusController({ isEnabled, isBusy, revalidate }) {
  let mounted = true;
  let inFlight = false;
  let pending = false;

  function pump() {
    if (!mounted) return;
    if (typeof isEnabled === "function" && !isEnabled()) return;
    if (typeof isBusy === "function" && isBusy()) {
      pending = true;
      return;
    }
    if (inFlight) {
      pending = true;
      return;
    }
    pending = false;
    inFlight = true;
    let result;
    try {
      result = revalidate();
    } catch (err) {
      inFlight = false;
      return;
    }
    const finish = () => {
      inFlight = false;
      if (!mounted) return;
      if (typeof isBusy === "function" && isBusy()) {
        pending = true;
        return;
      }
      if (pending) {
        pending = false;
        pump();
      }
    };
    if (result && typeof result.then === "function") {
      Promise.resolve(result).then(finish, finish);
    } else {
      finish();
    }
  }

  return {
    onFocus() {
      if (!mounted) return;
      pump();
    },
    /** Call when a mutation transitions from busy to idle. */
    notifySettled() {
      if (!mounted || !pending) return;
      pump();
    },
    dispose() {
      mounted = false;
      pending = false;
    },
  };
}

/** Shown by the surface that already owns a local error/banner. Not a toast system. */
export const FOCUS_REVALIDATION_UNRESOLVED_MESSAGE =
  "Не вдалося повторно перевірити доступ. Дії тимчасово недоступні.";

/**
 * Begin a background revalidation write lock.
 * Token only — no permission or HTTP interpretation.
 */
export function openBackgroundRevalidation(
  tokenRef,
  writesSuspendedRef,
  setWritesSuspended,
) {
  const next = (Number(tokenRef.current) || 0) + 1;
  tokenRef.current = next;
  if (writesSuspendedRef) writesSuspendedRef.current = true;
  setWritesSuspended(true);
  return next;
}

/**
 * CONFIRMED releases writes when this attempt is still current.
 * A foreground completion releases only if no newer background token exists.
 * UNRESOLVED and background authority-loss leave writes suspended.
 */
export function settleRevalidationWrites({
  keepVisible,
  confirmed,
  token,
  tokenAtStart,
  tokenRef,
  writesSuspendedRef,
  setWritesSuspended,
}) {
  const release = () => {
    if (writesSuspendedRef) writesSuspendedRef.current = false;
    setWritesSuspended(false);
  };
  if (confirmed) {
    if (token != null && token !== tokenRef.current) return;
    if (token == null && tokenAtStart != null && tokenRef.current !== tokenAtStart) {
      return;
    }
    release();
    return;
  }
  if (keepVisible) return;
  if (tokenAtStart != null && tokenRef.current !== tokenAtStart) return;
  release();
}

/**
 * @param {{
 *   enabled?: boolean,
 *   busy?: boolean,
 *   onRevalidate: () => (Promise<unknown> | void),
 * }} options
 */
export function useCompanyFocusRevalidation({
  enabled = true,
  busy = false,
  onRevalidate,
}) {
  const enabledRef = useRef(enabled);
  const busyRef = useRef(busy);
  const onRevalidateRef = useRef(onRevalidate);
  const controllerRef = useRef(null);
  enabledRef.current = enabled;
  busyRef.current = busy;
  onRevalidateRef.current = onRevalidate;

  useEffect(() => {
    const controller = createCompanyFocusController({
      isEnabled: () => enabledRef.current,
      isBusy: () => busyRef.current,
      revalidate: () => onRevalidateRef.current?.(),
    });
    controllerRef.current = controller;
    const onFocus = () => controller.onFocus();
    window.addEventListener("focus", onFocus);
    return () => {
      controller.dispose();
      if (controllerRef.current === controller) {
        controllerRef.current = null;
      }
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const previousBusyRef = useRef(busy);
  useEffect(() => {
    const wasBusy = previousBusyRef.current;
    previousBusyRef.current = busy;
    if (wasBusy && !busy) {
      controllerRef.current?.notifySettled();
    }
  }, [busy]);
}
